import {Component, ContentChild, Input, OnInit, TemplateRef} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule} from '@angular/forms';
import {SubjectFormBaseComponent} from '../subject-form-base.component';
import {ADDRESS_PATTERN, PERSON_NAME_PATTERN, POSTAL_PATTERN} from '../../../_constants/constants';
import {phoneValidator} from '../../../_utils/validators';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {RequiredComponent} from '../../../common/forms/required/required.component';
import {ValidationErrorsComponent} from '../../../common/forms/validation-errors/validation-errors.component';
import {AddressFormComponent} from '../../../common/forms/address-form/address-form.component';
import {PhoneFormRowComponent} from '../../../common/forms/phone-form-row/phone-form-row.component';
import {TranslateModule} from '@ngx-translate/core';

/**
 * There's a few classes/components involved with the main forms for consumer/ifs requests.  This is because there's a lot of forms that are similar to
 * each other but have small differences like validation changes or extra/missing fields.
 *
 * This class, and its parent {@link SubjectFormBaseComponent}, are used to try to handle most of the similarities between the forms.
 *
 * The area that changes the most is the identification section.  Each specific type of form will be a separate component that passes that section through
 * via the <ng-content /> mechanic.
 *
 * Since the form ends up being assembled by different components, form data gets passed up from the child components to the parent through an output param.
 * For an example of the flow of form data, see {@link ConsumerSubjectFormComponent} jsdoc
 *
 */
@Component({
    selector: 'app-subject-form',
    templateUrl: './standard-subject-form.component.html',
    styleUrls: ['./standard-subject-form.component.scss'],
    imports: [ReactiveFormsModule, NgClass, RequiredComponent, ValidationErrorsComponent, AddressFormComponent, PhoneFormRowComponent, NgTemplateOutlet, TranslateModule]
})
export class StandardSubjectFormComponent extends SubjectFormBaseComponent implements OnInit {

  @Input() enablePreviousAddress: boolean = false;
  @Input() enableEmployment: boolean = false;
  @Input() enableIdentificationHeader: boolean = false;

  /*
   * Child form groups will be inserted into the {@link SubjectFormBaseComponent.subjectFormGroup}
   * see: ngOnInit()
   */
  @Input() identificationFormGroup: UntypedFormGroup;
  @Input() currentAddressFormGroup: UntypedFormGroup;
  @Input() prevAddressFormGroup: UntypedFormGroup;

  /** The control name that will be used when the additionalFormContentFormGroup is inserted into the subjectFormGroup. */
  @Input() additionalFormControlName: string;

  /**
   * Define additional html to be included at the bottom of the standard subject form.
   * In the parent component, define a template with this identifier.
   * eg:
   *    <ng-template #additionalFormContent>
   *      <p>HELLO</p>
   *    </ng-template>
   */
  @ContentChild('additionalFormContent') additionalFormContent: TemplateRef<any>;

  /**
   * This is an Input() param, but is done through a method down below so that other logic can happen whenever this input is changed.
   * The matching formGroup for any additional form content defined in the {@link additionalFormContent} template
   */
  private _additionalFormContentFormGroup: UntypedFormGroup;

  constructor() {
    super();
  }

  /**
   *  FirstName: Required, <=16, PERSON_NAME_PATTERN <br />
   *  MiddleName: <=10, PERSON_NAME_PATTERN <br />
   *  LastName: Required, <=20, PERSON_NAME_PATTERN <br />
   *  Phone: <=16, phoneValidator
   */
  static buildSubjectFormGroup(
    formBuilder: UntypedFormBuilder,
    controlsUpdate: {
      firstName?: any; middleName?: any; lastName?: any; currentAddress?: any;
      prevAddress?: any; inclPrevAddress?: any; phone?: any; job?: any; employer?: any;
    } = {},
    options?
  ) {
    return formBuilder.group(Object.assign({
      firstName:       ['', Validators.compose([Validators.required, Validators.maxLength(16), Validators.pattern(PERSON_NAME_PATTERN)])],
      middleName:      ['', Validators.compose([Validators.maxLength(10), Validators.pattern(PERSON_NAME_PATTERN)])],
      lastName:        ['', Validators.compose([Validators.required, Validators.maxLength(20), Validators.pattern(PERSON_NAME_PATTERN)])],
      currentAddress:  null,
      prevAddress:     null,
      inclPrevAddress: null,
      phone:           ['', Validators.compose([Validators.maxLength(16), phoneValidator])],
      job:             null,
      employer:        null
    }, controlsUpdate), options);
  }

  /**
   *  Use streetNum for civic <br />
   *  <br />
   *  Street: Required, <=128, ADDRESS_PATTERN <br />
   *  City: Required, <=32, ADDRESS_PATTERN <br />
   *  Province: Required <br />
   *  Postal Code: Required, <=8, ADDRESS_PATTERN <br />
   */
  static buildAddressFormGroup(
    formBuilder: UntypedFormBuilder,
    controlsUpdate: {streetNum?: any; street?: any; apartmentNum?: any; city?: any; province?: any; postalCode?: any} = {}
  ): UntypedFormGroup {
    return formBuilder.group(Object.assign({
      street:     ['', Validators.compose([Validators.required, Validators.maxLength(128), Validators.pattern(ADDRESS_PATTERN)])],
      city:       ['', Validators.compose([Validators.required, Validators.maxLength(32), Validators.pattern(ADDRESS_PATTERN)])],
      province:   ['', Validators.required],
      postalCode: ['', Validators.compose([Validators.required, Validators.maxLength(8), Validators.pattern(POSTAL_PATTERN)])]
    }, controlsUpdate));
  }

  /**
   * We need to be able to pass ALL form data up to the parent via {@link SubjectFormBaseComponent.submittedData} output.
   * Which means we need to insert any formGroups that were passed in to this component into the subjectFormGroup as a sub control
   */
  ngOnInit() {
    console.info('Initializing standard subject form additional form groups');
    if(!this.subjectFormGroup) {
      console.warn('SubjectFormGroup not set!');
    }
    this.subjectFormGroup?.setControl('identification', this.identificationFormGroup);
    this.subjectFormGroup?.setControl('currentAddress', this.currentAddressFormGroup);
    this.subjectFormGroup?.setControl('prevAddress', this.prevAddressFormGroup);

    //The control for the additionalFormControlName is set in the Input() set method since it can be set programmatically well after the init
  }

  get additionalFormContentFormGroup(): UntypedFormGroup {
    return this._additionalFormContentFormGroup;
  }

  @Input()
  set additionalFormContentFormGroup(value: UntypedFormGroup) {
    this._additionalFormContentFormGroup = value;
    if(this._additionalFormContentFormGroup) {
      this.subjectFormGroup?.setControl(this.additionalFormControlName, this._additionalFormContentFormGroup);
    } else {
      this.subjectFormGroup?.removeControl(this.additionalFormControlName);
    }
  }

  get currentAddressF(): UntypedFormGroup {
    return this.f.controls.currentAddress as UntypedFormGroup;
  }

  set currentAddressF(formGroup: UntypedFormGroup) {
    console.debug('Updating current address form group...');
    this.f.setControl('currentAddress', formGroup);
  }

  get prevAddressF(): UntypedFormGroup {
    return this.f.controls.prevAddress as UntypedFormGroup;
  }

  set prevAddressF(formGroup: UntypedFormGroup) {
    this.f.setControl('prevAddress', formGroup);
  }

}
